import React from "react";
import { motion } from "framer-motion";
import Lottie from "lottie-react";
import { Link } from "react-router-dom";
import {
  FaUser,
  FaUserTie,
  FaRocket,
  FaBriefcase,
  FaCalendarCheck,
  FaUsers,
  FaSearchPlus,
  FaChartLine
} from "react-icons/fa";

import hrmsAnimation from "../../Animation/Welcome.json";
import companyLogo from "../../assets/Vector.png";

export default function HomePages() {
  return (
    <div className="min-h-screen w-full flex flex-col bg-gradient-to-br from-[#0b0f19] via-[#111827] to-[#1e1b4b] text-gray-100 font-sans selection:bg-indigo-500 selection:text-white overflow-x-hidden">

      {/* BACKGROUND DECORATIVE GLOWS */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[60%] rounded-full bg-indigo-500/10 blur-[120px]" />
        <div className="absolute bottom-[20%] right-[-10%] w-[45%] h-[55%] rounded-full bg-purple-500/10 blur-[120px]" />
      </div>

      {/* HEADER */}
      <header className="sticky top-0 w-full bg-white/5 backdrop-blur-xl border-b border-white/10 z-50 transition-all duration-300">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 cursor-pointer"
          >
            <img
              src={companyLogo}
              alt="Morgan Logo"
              className="h-10 md:h-12 object-contain drop-shadow-glow"
            />
            <div className="hidden sm:block">
              <span className="text-sm font-black tracking-wider bg-gradient-to-r from-white via-indigo-100 to-indigo-300 bg-clip-text text-transparent">
                MORGANS PLACEMENT
              </span>
              <p className="text-[10px] text-gray-400 font-bold tracking-widest uppercase">Consultancy & Consultancy</p>
            </div>
          </motion.div>

          {/* Right Action buttons */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 md:gap-4"
          >
            <Link
              to="/login"
              className="flex items-center gap-2 text-xs md:text-sm text-gray-300 hover:text-white px-4 py-2.5 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all duration-200 font-bold"
            >
              <FaUser className="w-3 h-3 text-indigo-400" />
              Employee Login
            </Link>

            <Link
              to="/adminlogin"
              className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 transition px-5 py-2.5 rounded-2xl text-xs md:text-sm text-white font-bold shadow-lg shadow-indigo-650/30 hover:shadow-indigo-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
            >
              <FaUserTie className="w-3 h-3" />
              Admin Portal
            </Link>
          </motion.div>
        </div>
      </header>

      {/* HERO SECTION */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-12 pb-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center flex-grow">
        {/* Left Copy Panel */}
        <div className="lg:col-span-7 space-y-8 text-left">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-2 bg-indigo-500/10 border border-indigo-500/30 rounded-full px-4 py-1.5 text-xs font-bold text-indigo-300 uppercase tracking-wider"
          >
            <FaRocket className="w-3.5 h-3.5 animate-bounce" />
            Next-Gen HRMS & ATS Platform
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="space-y-4"
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black leading-[1.1] tracking-tight">
              Scale Your Talent <br />
              <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                Pipeline Effortlessly.
              </span>
            </h1>
            <p className="text-gray-400 text-sm sm:text-base md:text-lg font-medium leading-relaxed max-w-2xl">
              Empower your recruitment team with Morgans HRMS. Source candidate profiles instantly using smart Boolean queries, manage stages in a fluid Ceipal-style candidate pipeline, and monitor key closure statistics—all under a single premium workspace.
            </p>
          </motion.div>


          {/* Stats Bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="grid grid-cols-3 gap-6 pt-8 border-t border-white/5"
          >
            <div>
              <p className="text-2xl sm:text-3xl font-black text-white">60x</p>
              <p className="text-[11px] text-gray-500 font-extrabold uppercase mt-1 tracking-wider">Search Sourced</p>
            </div>
            <div>
              <p className="text-2xl sm:text-3xl font-black text-white">99%</p>
              <p className="text-[11px] text-gray-500 font-extrabold uppercase mt-1 tracking-wider">Placement Rate</p>
            </div>
            <div>
              <p className="text-2xl sm:text-3xl font-black text-white">5000+</p>
              <p className="text-[11px] text-gray-500 font-extrabold uppercase mt-1 tracking-wider">Candidates Joined</p>
            </div>
          </motion.div>
        </div>

        {/* Right Animation Widget */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, rotate: 1 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1 }}
          className="lg:col-span-5 flex justify-center relative"
        >
          <div className="absolute inset-0 bg-gradient-to-tr from-indigo-500/20 to-purple-500/20 rounded-[40px] blur-3xl -z-10" />
          <div className="bg-white/5 border border-white/10 p-8 rounded-[40px] shadow-2xl backdrop-blur-md w-full max-w-md flex flex-col items-center">
            <Lottie animationData={hrmsAnimation} loop={true} className="h-64 sm:h-72 object-contain" />
            <div className="w-full text-center mt-6">
              <span className="text-xs font-bold text-gray-400">Trusted by modern placement departments worldwide</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* CORE MODULES / FEATURES SECTION */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-16 md:py-24 border-t border-white/5 space-y-12">
        <div className="text-center max-w-2xl mx-auto space-y-4">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black">Everything you need to source & manage.</h2>
          <p className="text-gray-400 text-xs sm:text-sm font-medium">Simplify sourcing operations and employee management in one comprehensive suite.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Card 1 */}
          <div className="bg-white/5 border border-white/10 hover:border-white/20 p-6 rounded-3xl space-y-4 hover:-translate-y-1 transition duration-200">
            <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-400 text-xl">
              <FaSearchPlus />
            </div>
            <h3 className="text-base font-bold text-white">Boolean Talent Pool</h3>
            <p className="text-gray-450 text-xs leading-relaxed">
              Find the best profiles in milliseconds. Scan candidate records via complex keyword and boolean logic.
            </p>
          </div>

          {/* Card 2 */}
          <div className="bg-white/5 border border-white/10 hover:border-white/20 p-6 rounded-3xl space-y-4 hover:-translate-y-1 transition duration-200">
            <div className="w-12 h-12 rounded-2xl bg-purple-500/10 flex items-center justify-center text-purple-400 text-xl">
              <FaUsers />
            </div>
            <h3 className="text-base font-bold text-white">ATS Candidate Pipeline</h3>
            <p className="text-gray-450 text-xs leading-relaxed">
              Ceipal-style candidate stage tracking with drag dropdowns, scheduler modals, and integrated dynamic resume viewer panels.
            </p>
          </div>

          {/* Card 3 */}
          <div className="bg-white/5 border border-white/10 hover:border-white/20 p-6 rounded-3xl space-y-4 hover:-translate-y-1 transition duration-200">
            <div className="w-12 h-12 rounded-2xl bg-pink-500/10 flex items-center justify-center text-pink-400 text-xl">
              <FaCalendarCheck />
            </div>
            <h3 className="text-base font-bold text-white">Leave & Attendance</h3>
            <p className="text-gray-450 text-xs leading-relaxed">
              Easily manage recruiter daily attendance logs, logouts, leaves, calendar dates, and direct structural permissions.
            </p>
          </div>

          {/* Card 4 */}
          <div className="bg-white/5 border border-white/10 hover:border-white/20 p-6 rounded-3xl space-y-4 hover:-translate-y-1 transition duration-200">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-400 text-xl">
              <FaChartLine />
            </div>
            <h3 className="text-base font-bold text-white">Analytical Reporting</h3>
            <p className="text-gray-450 text-xs leading-relaxed">
              Visualize submission ratios, daily closure logs, position matrix charts, and department performance targets in real-time.
            </p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="w-full bg-[#070a12]/80 border-t border-white/5 py-8 mt-auto relative z-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={companyLogo} alt="Morgan Logo" className="h-8 object-contain" />
            <span className="text-xs text-gray-500 font-bold">© {new Date().getFullYear()} Morgans Placement & Consultancy. All rights reserved.</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-gray-500 font-bold">
            <Link to="/login" className="hover:text-indigo-400 transition">Employee Portal</Link>
            <Link to="/adminlogin" className="hover:text-indigo-400 transition">Admin Portal</Link>
            {/* <Link to="/seejob" className="hover:text-indigo-400 transition">Career Openings</Link> */}
          </div>
        </div>
      </footer>

    </div>
  );
}
